import HyperDiv from "components/ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv"
import useScreenType, {
	SCREEN_MIN_WIDTHS,
} from "components/ProjectNonSpecificComponents/Hooks/useScreenType"
import { observer } from "mobx-react"
import React, { useEffect } from "react"
import { useHistory } from "react-router-dom"
import AppRequests from "../API/AppRequests/AppRequests"
import ROUTES from "../Constants/ROUTES"
import { useFormStore } from "../Contexts/FormStoreContext"
import { useStore } from "../Contexts/GlobalStoreContext"
import ConnectionFormSection from "../Elements/AdminForm/organisms/RequestSections/ConnectionFormSection"
import Button from "../Elements/Global/atoms/Buttons/Button/Button"
import { ISettings } from "../Interfaces/FormsInterface"
import { IGET_Connection_Check } from "../Interfaces/DataInterface"
import CenteredContentTemplate from "../Templates/CenteredContentTemplate"
import { HEIGHT_WITH_NAV_AND_FOOTER } from "../Constants/GLOBAL_CONSTANTS"
import { lostOrFinishedAdminSession } from "../API/Functions/lostOrFinishedAdminSession"
import { useTranslation } from "react-i18next"

interface Props {}

/**
 * User opens helpdeks
 * System checks previous saved settings (userSettings.json)
 * Tries to connect with settings
 * If fails, user inputs form
 * After form is completed
 * Settings are saved
 * Connection is checked again
 */
const ConnectionPage = observer(({}: Props) => {
	const { t } = useTranslation()
	const store = useStore()
	const formStore = useFormStore()
	const history = useHistory()
	const { isMobile } = useScreenType()

	useEffect(() => {
		if (!store.changeConnection) {
			store.setLoaderOverlayOpen(true)
			fetchSavedSettingsAndCheckConnection()
		}
	}, [])

	const fetchSavedSettingsAndCheckConnection = async () => {
		const { ok, data, statusCode } = await AppRequests("Setting").get<ISettings>(
			"get-settings-admin"
		)
		store.triggerSessionTimeout()

		if (ok && data) {
			formStore.mergeSettingsDataSafely(data)
			tryToReconnect()
		} else if (statusCode == 404) {
			saveDefaultSettings()
		} else if (statusCode == 401) {
			lostOrFinishedAdminSession(history, store)
		} else {
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "settings.failed",
				descriptionKey: "settings.failedDescription",
			})
			store.setLoaderOverlayOpen(false)
		}
	}

	const tryToReconnect = async () => {
		if (store.changeConnection) return
		const isConnectionSuccessful: boolean = await reconnectFromUserSettings()
		if (isConnectionSuccessful) {
			store.setHasConnectionBeenMade(true)
			if (formStore.selectedFormsConfig) {
				store.setSelectedToolId(formStore.selectedFormsConfig.itemType.id)
			}
			store.setInfoModalConfig({
				titleKey: "connection.success",
				descriptionKey: "connection.successDescription",
				type: "info",
				showDuration: 5000,
			})
			history.push(ROUTES.REQUEST_CNFG)
		} else {
			sessionStorage.removeItem("checkConnection")
			store.setHasConnectionBeenMade(false)
			store.setSelectedToolId(undefined)
			store.setInfoModalConfig({
				titleKey: "connection.failed",
				type: "fail",
			})
			store.setLoaderOverlayOpen(false)
		}
	}

	const reconnectFromUserSettings = async (): Promise<boolean> => {
		const { ok, data } = await AppRequests("Connection").get<IGET_Connection_Check>("check")

		if (ok && data?.isOk) {
			sessionStorage.setItem("checkConnection", "true")
			return true
		} else {
			return false
		}
	}

	const saveConnectionData = async () => {
		const { ok } = await AppRequests("Connection").post(null, formStore.settingsData.connection)

		if (ok) {
			store.setChangeConnection(false)
			tryToReconnect()
		} else {
			store.setInfoModalConfig({
				titleKey: "connection.failed",
				type: "fail",
			})
		}
	}

	const saveDefaultSettings = async () => {
		const { ok } = await AppRequests("Setting").post("save-settings", formStore.settingsData)
		if (!ok) {
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "settings.failedTotal",
				descriptionKey: "settings.totalFail",
			})
		}
		store.setLoaderOverlayOpen(false)
	}

	return (
		<CenteredContentTemplate>
			<HyperDiv
				width={SCREEN_MIN_WIDTHS.SMALL_TABLET}
				style={{ overflow: "hidden auto" }}
				height={HEIGHT_WITH_NAV_AND_FOOTER}
			>
				<ConnectionFormSection
					submitButton={
						<Button
							style={{ width: isMobile ? 160 : 320, height: 46, maxWidth: "90vw" }}
							onClick={saveConnectionData}
							children={t("button.connect")}
						/>
					}
				/>
			</HyperDiv>
		</CenteredContentTemplate>
	)
})

export default ConnectionPage
